import { useState, useEffect, useCallback, useRef } from 'react'
import './progressBar.scss'

// Singleton state management for global progress bar control
let progressState = {
	isVisible: false,
	progress: 0,
	requestCount: 0
}

type ProgressListener = (state: typeof progressState) => void
const listeners: Set<ProgressListener> = new Set()

const notifyListeners = () => {
	listeners.forEach(listener => listener({ ...progressState }))
}

// Increment function that slows down as it approaches 100%
const calculateIncrement = (current: number): number => {
	if (current < 20) return 10
	if (current < 50) return 4
	if (current < 80) return 2
	if (current < 99) return 0.5
	return 0
}

// Global progress control API
export const progress = {
	start: () => {
		progressState.requestCount++
		if (!progressState.isVisible) {
			progressState.isVisible = true
			progressState.progress = 0
		}
		notifyListeners()
	},

	done: () => {
		progressState.requestCount = Math.max(0, progressState.requestCount - 1)
		if (progressState.requestCount === 0) {
			progressState.progress = 100
			notifyListeners()
			// Fade out after completion animation
			setTimeout(() => {
				progressState.isVisible = false
				progressState.progress = 0
				notifyListeners()
			}, 300)
		}
	},

	inc: () => {
		if (progressState.isVisible && progressState.progress < 99) {
			const increment = calculateIncrement(progressState.progress)
			progressState.progress = Math.min(99, progressState.progress + increment)
			notifyListeners()
		}
	},

	set: (value: number) => {
		progressState.progress = Math.min(100, Math.max(0, value))
		notifyListeners()
	},

	// Force complete (useful for error cases)
	forceComplete: () => {
		progressState.requestCount = 0
		progressState.progress = 100
		notifyListeners()
		setTimeout(() => {
			progressState.isVisible = false
			progressState.progress = 0
			notifyListeners()
		}, 300)
	}
}

// React component for rendering the progress bar
const ProgressBar = () => {
	const [state, setState] = useState(progressState)
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

	// Subscribe to progress state changes
	useEffect(() => {
		const listener: ProgressListener = (newState) => {
			setState({ ...newState })
		}
		listeners.add(listener)
		return () => {
			listeners.delete(listener)
		}
	}, [])

	// Auto-increment timer
	useEffect(() => {
		if (state.isVisible && state.progress < 99) {
			intervalRef.current = setInterval(() => {
				progress.inc()
			}, 300)
		} else if (intervalRef.current) {
			clearInterval(intervalRef.current)
			intervalRef.current = null
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current)
			}
		}
	}, [state.isVisible, state.progress])

	if (!state.isVisible && state.progress === 0) {
		return null
	}

	const isComplete = state.progress === 100

	return (
		<div className={`progress-bar-container ${isComplete ? 'complete' : ''}`}>
			<div 
				className="progress-bar"
				style={{ 
					transform: `translateX(${state.progress - 100}%)`,
					transition: isComplete 
						? 'transform 0.2s ease-out' 
						: 'transform 0.3s ease'
				}}
			>
				<div className="progress-bar-glow" />
			</div>
		</div>
	)
}

export default ProgressBar
